import { z } from 'zod';
import {
    buildStoredJsonParser,
    defineStoredJsonContract,
    type StoredJsonRowBoundary,
} from '../../stored-json-contract.types';
import {
    type AchPaymentGatewayLifecycleRequest,
    type BinLookupGatewayLifecycleRequest,
    type BinLookupEvidenceV1,
    type AchPaymentGatewayRequest,
    type CardPointeCnpTokenSaleGatewayLifecycleRequest,
    type CardPointeRawGatewayLifecycleRequest,
    type CardPointeInquireGatewayLifecycleRequest,
    type CardPointeLifecycleFinalizationGatewayLifecycleRequest,
    type CardPointeLifecyclePreflightGatewayLifecycleRequest,
    type CardPointeReconciliationGatewayLifecycleRequest,
    type CardPointeRefundGatewayLifecycleRequest,
    type CardPointeStoredCardSaleGatewayLifecycleRequestV1,
    type CardPointeStoredCardSaleGatewayLifecycleRequestV2,
    type CardPointeTerminalSaleGatewayLifecycleRequestV1,
    type CardPointeTerminalSaleGatewayLifecycleRequestV2,
    type CardPointeTerminalTokenizationGatewayLifecycleRequest,
    type CardPointeTransactionStatusGatewayLifecycleRequest,
    type CardPointeVoidGatewayLifecycleRequest,
    type CloverCnpTokenSaleGatewayLifecycleRequest,
    type CloverLifecycleFinalizationGatewayLifecycleRequest,
    type CloverLifecyclePreflightGatewayLifecycleRequest,
    type CloverReconciliationGatewayLifecycleRequest,
    type CloverRawGatewayLifecycleRequest,
    type ProviderGatewayRequestSnapshot,
    type CloverRefundGatewayLifecycleRequest,
    type CloverStoredCardSaleGatewayLifecycleRequest,
    type CloverTerminalSaleGatewayLifecycleRequest,
    type CloverTerminalTokenizationGatewayLifecycleRequest,
    type CloverTransactionStatusGatewayLifecycleRequest,
    type CloverVoidGatewayLifecycleRequest,
    GatewayLifecycleRequestType,
    type GatewayLifecycleRequest,
    type IntegrationReconciliationGatewayLifecycleRequest,
    type IntegrationReconciliationGatewayRequest,
    type TerminalSaleDispatchGatewayRequest,
    type TerminalSaleDispatchSourceDocumentKeySnapshot,
    type TerminalSaleDispatchGatewayLifecycleRequest,
    type TerminalSaleDispatchTransactionDetailsSnapshot,
    type ValorAdditionalPrompt,
    type ValorCardTokenSaleGatewayLifecycleRequest,
    type ValorCardTokenSaleGatewayRequest,
    type ValorReconciliationGatewayLifecycleRequest,
    type ValorReconciliationGatewayRequest,
    type ValorRefundGatewayLifecycleRequest,
    type ValorRefundGatewayRequest,
    type ValorRefundPrompt,
    type ValorRawGatewayLifecycleRequest,
    type ValorTerminalPublishGatewayLifecycleRequestV1,
    type ValorTerminalPublishGatewayLifecycleRequestV2,
    type ValorTerminalPublishGatewayRequestV1,
    type ValorTerminalPublishGatewayRequestV2,
    type ValorTerminalPublishPayloadV1,
    type ValorTerminalPublishPayloadV2,
    type ValorTransactionListGatewayLifecycleRequest,
    type ValorTransactionListGatewayRequest,
    type ValorTransactionStatusGatewayLifecycleRequest,
    type ValorTransactionStatusGatewayRequest,
    type ValorVoidGatewayLifecycleRequest,
    type ValorVoidGatewayRequest,
} from '../../types/ipg/gateway-lifecycle-request.types';
import { BinLookupBackend } from '../../backend_config.types';
import { CardType } from '../../samba-payment.types';
import { ThirdPartySystemId } from '../../types/third-party-system.enums';
import {
    JsonLosslessNumberInputSchema,
    JsonObjectSchema,
    JsonValueSchema,
} from '../common.schemas';
import {
    CardPointeCnpTokenSaleAmountSchema,
    CardPointeCnpTokenSaleRequestDetailsSchema,
} from './cardpointe/cardpointe-cnp-token-sale.schemas';
import { CardPointeLifecycleFinalizationRequestPayloadSchema } from './cardpointe/cardpointe-lifecycle-finalization.schemas';
import { StoredCardPointeLifecyclePreflightRequestPayloadSchema } from './cardpointe/cardpointe-lifecycle-preflight.schemas';
import { CardPointeReconciliationLookupRequestDetailsSchema } from './cardpointe/cardpointe-reconciliation.schemas';
import { CardPointeRefundRequestDetailsSchema } from './cardpointe/cardpointe-refund.schemas';
import type {
    CardPointeStoredCardSaleLegacyRequestBody,
    CardPointeStoredCardSaleLegacyRequestDetails,
    StoredCardPointeStoredCardSaleRequestDetails,
} from '../../types/ipg/cardpointe/cardpointe-stored-card-sale.types';
import {
    CardPointeTerminalSaleRequestLifecyclePayloadV2Schema,
    StoredCardPointeTerminalSaleRequestLifecyclePayloadV1Schema,
} from './cardpointe/cardpointe-terminal-sale-lifecycle.schemas';
import { CardPointeTerminalTokenizationRequestDetailsSchema } from './cardpointe/cardpointe-terminal-tokenization.schemas';
import { CardPointeTransactionStatusRequestDetailsSchema } from './cardpointe/cardpointe-transaction-status.schemas';
import { CardPointeTransactionDetailsRequestDetailsSchema } from './cardpointe/cardpointe-transaction-details.schemas';
import { CardPointeVoidRequestDetailsSchema } from './cardpointe/cardpointe-void.schemas';
import { CloverCnpTokenSaleRequestDetailsSchema } from './clover/clover-cnp-token-sale.schemas';
import { CloverLifecycleFinalizationRequestPayloadSchema } from './clover/clover-lifecycle-finalization.schemas';
import { CloverLifecyclePreflightRequestPayloadSchema } from './clover/clover-lifecycle-preflight.schemas';
import { CloverReconciliationLookupRequestDetailsSchema } from './clover/clover-reconciliation.schemas';
import { CloverRefundRequestDetailsSchema } from './clover/clover-refund.schemas';
import { CloverRefundKind } from '../../types/ipg/clover/clover-refund.types';
import { CloverTerminalSaleRequestLifecyclePayloadSchema } from './clover/clover-terminal-sale-lifecycle.schemas';
import { CloverTerminalTokenizationRequestDetailsSchema } from './clover/clover-terminal-tokenization.schemas';
import { CloverTransactionStatusRequestDetailsSchema } from './clover/clover-transaction-status.schemas';
import { CloverVoidRequestDetailsSchema } from './clover/clover-void.schemas';
import {
    defineSafeIntegerDomain,
    normalizeStoredJsonLosslessNumberFieldsAtPath,
    normalizeStoredJsonSafeIntegerFieldsAtPath,
    StoredJsonNumberRecordSchema,
    StoredJsonObjectSchema,
} from '../../stored-json-number';

const CloverMinorUnitMaximum = 999999999;
const CloverTimeoutMaximum = 300;

const ValorSurchargeIndicatorSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'gateway-lifecycle-request.valor.surcharge-indicator',
    provenance: {
        kind: 'finite-integer-domain',
        values: [0, 1],
    },
});

const ValorVoidSurchargeIndicatorSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'gateway-lifecycle-request.valor-void.surcharge-indicator',
    provenance: {
        kind: 'finite-integer-domain',
        values: [1],
    },
});

const TransactionDetailsIdentifierSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'gateway-lifecycle-request.transaction-details-identifier',
    provenance: {
        kind: 'bounded-owned-schema',
        schemaName: 'TerminalSaleDispatch transaction details identifier fields',
        minimum: Number.MIN_SAFE_INTEGER,
        maximum: Number.MAX_SAFE_INTEGER,
    },
});

const IpwSystemIdentifierSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'gateway-lifecycle-request.ipw_system_id',
    provenance: {
        kind: 'finite-integer-domain',
        values: Object.values(ThirdPartySystemId),
    },
});

const CloverTimeoutSecondsSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'gateway-lifecycle-request.clover.timeout-seconds',
    provenance: {
        kind: 'bounded-owned-schema',
        schemaName: 'Clover request timeoutSeconds fields',
        minimum: 0,
        maximum: CloverTimeoutMaximum,
    },
});

const CloverMinorUnitSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'gateway-lifecycle-request.clover.minor-unit-amount',
    provenance: {
        kind: 'bounded-owned-schema',
        schemaName: 'Clover request amount fields',
        minimum: 0,
        maximum: CloverMinorUnitMaximum,
    },
});

const CloverPositiveMinorUnitSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'gateway-lifecycle-request.clover.positive-minor-unit-amount',
    provenance: {
        kind: 'bounded-owned-schema',
        schemaName: 'Clover positive request amount fields',
        minimum: 1,
        maximum: CloverMinorUnitMaximum,
    },
});

const BinLookupEvidenceVersionSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'gateway-lifecycle-request.bin-lookup.bin_lookup_evidence_version',
    provenance: {
        kind: 'finite-integer-domain',
        values: [1],
    },
});

const HttpStatusSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'gateway-lifecycle-request.http-status',
    provenance: {
        kind: 'bounded-owned-schema',
        schemaName: 'Gateway response details HTTP status',
        minimum: 100,
        maximum: 599,
    },
});

function normalizeStoredValorCardTokenSaleRequest(value: unknown): unknown {
    const normalizedFlag = normalizeStoredJsonSafeIntegerFieldsAtPath(value, {
        safeIntegerDomain: ValorSurchargeIndicatorSafeIntegerDomain,
        path: [],
        fields: ['surchargeIndicator'],
    });
    return normalizeStoredJsonLosslessNumberFieldsAtPath(normalizedFlag, {
        path: [],
        fields: ['surchargeAmount'],
    });
}

function normalizeStoredValorVoidRequest(value: unknown): unknown {
    return normalizeStoredJsonSafeIntegerFieldsAtPath(value, {
        safeIntegerDomain: ValorVoidSurchargeIndicatorSafeIntegerDomain,
        path: [],
        fields: ['surchargeindicator'],
    });
}

function normalizeStoredValorRefundRequest(value: unknown): unknown {
    const normalizedFlag = normalizeStoredJsonSafeIntegerFieldsAtPath(value, {
        safeIntegerDomain: ValorSurchargeIndicatorSafeIntegerDomain,
        path: [],
        fields: ['surchargeIndicator'],
    });
    return normalizeStoredJsonLosslessNumberFieldsAtPath(normalizedFlag, {
        path: [],
        fields: ['amount'],
    });
}

function normalizeStoredTerminalSaleDispatchRequest(value: unknown): unknown {
    const normalizedSourceKey = normalizeStoredJsonSafeIntegerFieldsAtPath(value, {
        safeIntegerDomain: TransactionDetailsIdentifierSafeIntegerDomain,
        path: ['source_document_key'],
        fields: ['transaction_details_type_id', 'transaction_details_type_version'],
    });
    const normalizedDetailIdentifiers = normalizeStoredJsonSafeIntegerFieldsAtPath(
        normalizedSourceKey,
        {
            safeIntegerDomain: TransactionDetailsIdentifierSafeIntegerDomain,
            path: ['transaction_details'],
            fields: ['transaction_details_type_id', 'transaction_details_type_version'],
        }
    );
    const normalizedDetails = normalizeStoredJsonSafeIntegerFieldsAtPath(
        normalizedDetailIdentifiers,
        {
            safeIntegerDomain: IpwSystemIdentifierSafeIntegerDomain,
            path: ['transaction_details'],
            fields: ['ipw_system_id'],
        }
    );
    return normalizeStoredJsonLosslessNumberFieldsAtPath(normalizedDetails, {
        path: ['transaction_details'],
        fields: ['amount'],
    });
}

function normalizeStoredCloverTerminalSaleRequestDetails(value: unknown): unknown {
    const normalizedHeaders = normalizeStoredJsonSafeIntegerFieldsAtPath(value, {
        safeIntegerDomain: CloverTimeoutSecondsSafeIntegerDomain,
        path: ['headers'],
        fields: ['timeoutSeconds'],
    });
    return normalizeStoredJsonSafeIntegerFieldsAtPath(normalizedHeaders, {
        safeIntegerDomain: CloverMinorUnitSafeIntegerDomain,
        path: ['body'],
        fields: ['amount', 'tipAmount'],
    });
}

function normalizeStoredCloverRequestHeaders(value: unknown, path: readonly string[]): unknown {
    return normalizeStoredJsonSafeIntegerFieldsAtPath(value, {
        safeIntegerDomain: CloverTimeoutSecondsSafeIntegerDomain,
        path,
        fields: ['timeoutSeconds'],
    });
}

function normalizeStoredCloverChargeRequest(value: unknown): unknown {
    return normalizeStoredJsonSafeIntegerFieldsAtPath(value, {
        safeIntegerDomain: CloverMinorUnitSafeIntegerDomain,
        path: ['body'],
        fields: ['amount'],
    });
}

function normalizeStoredGatewayRequestPayload(requestType: unknown, payload: unknown): unknown {
    switch (requestType) {
        case GatewayLifecycleRequestType.BIN_LOOKUP:
            return normalizeStoredJsonSafeIntegerFieldsAtPath(payload, {
                safeIntegerDomain: BinLookupEvidenceVersionSafeIntegerDomain,
                path: [],
                fields: ['bin_lookup_evidence_version'],
            });
        case GatewayLifecycleRequestType.VALOR_CARD_TOKEN_SALE:
            return normalizeStoredValorCardTokenSaleRequest(payload);
        case GatewayLifecycleRequestType.VALOR_VOID:
            return normalizeStoredValorVoidRequest(payload);
        case GatewayLifecycleRequestType.VALOR_REFUND:
            return normalizeStoredValorRefundRequest(payload);
        case GatewayLifecycleRequestType.TERMINAL_SALE_DISPATCH:
            return normalizeStoredTerminalSaleDispatchRequest(payload);
        case GatewayLifecycleRequestType.CLOVER_CNP_TOKEN_SALE:
        case GatewayLifecycleRequestType.CLOVER_STORED_CARD_SALE:
            return normalizeStoredCloverChargeRequest(payload);
        case GatewayLifecycleRequestType.CLOVER_TERMINAL_SALE:
            return normalizeStoredCloverTerminalSalePayload(payload);
        case GatewayLifecycleRequestType.CLOVER_LIFECYCLE_PREFLIGHT:
            return normalizeStoredCloverPreflightPayload(payload);
        case GatewayLifecycleRequestType.CARDPOINTE_LIFECYCLE_FINALIZATION:
        case GatewayLifecycleRequestType.CLOVER_LIFECYCLE_FINALIZATION:
            return normalizeStoredTerminalFinalizationPayload(payload);
        case GatewayLifecycleRequestType.CLOVER_TERMINAL_TOKENIZATION:
        case GatewayLifecycleRequestType.CLOVER_TRANSACTION_STATUS:
        case GatewayLifecycleRequestType.CLOVER_VOID:
            return normalizeStoredCloverRequestHeaders(payload, ['headers']);
        case GatewayLifecycleRequestType.CLOVER_REFUND:
            return normalizeStoredCloverRefundRequest(payload);
        case GatewayLifecycleRequestType.CLOVER_RECONCILIATION:
            return normalizeStoredCloverRequestHeaders(payload, [
                'statusRequestDetails',
                'headers',
            ]);
        default:
            return payload;
    }
}

function normalizeStoredCloverTerminalSalePayload(value: unknown): unknown {
    const rootResult = StoredJsonNumberRecordSchema.safeParse(value);
    if (!rootResult.success) {
        return value;
    }
    return {
        ...rootResult.data,
        request_details: normalizeStoredCloverTerminalSaleRequestDetails(
            rootResult.data.request_details
        ),
    };
}

function normalizeStoredCloverPreflightPayload(value: unknown): unknown {
    const rootResult = StoredJsonNumberRecordSchema.safeParse(value);
    const requestDetailsResult = StoredJsonNumberRecordSchema.safeParse(
        rootResult.success ? rootResult.data.request_details : undefined
    );
    if (!rootResult.success || !requestDetailsResult.success) {
        return value;
    }
    return {
        ...rootResult.data,
        request_details: {
            ...requestDetailsResult.data,
            terminal_sale_request_details: normalizeStoredCloverTerminalSaleRequestDetails(
                requestDetailsResult.data.terminal_sale_request_details
            ),
        },
    };
}

function normalizeStoredTerminalFinalizationPayload(value: unknown): unknown {
    const normalizedHttpStatus = normalizeStoredJsonSafeIntegerFieldsAtPath(value, {
        safeIntegerDomain: HttpStatusSafeIntegerDomain,
        path: ['request_details', 'terminal_sale_response_details'],
        fields: ['httpStatus'],
    });
    const rootResult = StoredJsonNumberRecordSchema.safeParse(normalizedHttpStatus);
    const requestDetailsResult = StoredJsonNumberRecordSchema.safeParse(
        rootResult.success ? rootResult.data.request_details : undefined
    );
    const responseDetailsResult = StoredJsonNumberRecordSchema.safeParse(
        requestDetailsResult.success
            ? requestDetailsResult.data.terminal_sale_response_details
            : undefined
    );
    if (
        !responseDetailsResult.success ||
        (responseDetailsResult.data.outcome !== 'approved' &&
            responseDetailsResult.data.outcome !== 'declined')
    ) {
        return normalizedHttpStatus;
    }
    return normalizeStoredJsonSafeIntegerFieldsAtPath(normalizedHttpStatus, {
        safeIntegerDomain: CloverPositiveMinorUnitSafeIntegerDomain,
        path: ['request_details', 'terminal_sale_response_details', 'body', 'payment'],
        fields: ['amount'],
    });
}

function normalizeStoredCloverRefundRequest(value: unknown): unknown {
    const normalizedHeaders = normalizeStoredCloverRequestHeaders(value, ['headers']);
    const normalizedRequest = normalizeStoredJsonSafeIntegerFieldsAtPath(normalizedHeaders, {
        safeIntegerDomain: CloverPositiveMinorUnitSafeIntegerDomain,
        path: ['body'],
        fields: ['amount'],
    });
    const requestResult = StoredJsonNumberRecordSchema.safeParse(normalizedRequest);
    return requestResult.success && requestResult.data.kind === undefined
        ? { ...requestResult.data, kind: CloverRefundKind.CLOUD_PAY }
        : normalizedRequest;
}

export function normalizeStoredGatewayLifecycleRequestNumericLeaves(value: unknown): unknown {
    const rootResult = StoredJsonNumberRecordSchema.safeParse(value);
    if (!rootResult.success) {
        return value;
    }
    const normalizedPayload = normalizeStoredGatewayRequestPayload(
        rootResult.data.gateway_request_type,
        rootResult.data.gateway_request
    );
    if (normalizedPayload === rootResult.data.gateway_request) {
        return value;
    }
    return {
        ...rootResult.data,
        gateway_request: normalizedPayload,
    };
}

type GatewayLifecycleRequestVariantSchema<
    TRequestType extends GatewayLifecycleRequestType,
    TGatewayRequestSchema extends z.ZodType,
> = z.ZodObject<{
    gateway_request_type: z.ZodLiteral<TRequestType>;
    gateway_request: TGatewayRequestSchema;
}>;

function gatewayLifecycleRequestVariantSchema<
    TRequestType extends GatewayLifecycleRequestType,
    TGatewayRequestSchema extends z.ZodType,
>(
    gatewayRequestType: TRequestType,
    gatewayRequestSchema: TGatewayRequestSchema
): GatewayLifecycleRequestVariantSchema<TRequestType, TGatewayRequestSchema> {
    return z.object({
        gateway_request_type: z.literal(gatewayRequestType),
        gateway_request: gatewayRequestSchema,
    });
}

export const ValorAdditionalPromptSchema: z.ZodType<ValorAdditionalPrompt> = z.object({
    PROMPT_NAME: z.string().min(1),
    PROMPT_VALUE: z.string().min(1),
});

export const ValorTerminalPublishPayloadV1Schema = z.object({
    TRAN_MODE: z.string().min(1),
    TRAN_CODE: z.string().min(1),
    AMOUNT: z.string().min(1),
    REQ_TXN_ID: z.string().min(1),
    INVOICENUMBER: z.string().min(1).optional(),
    VAS_LABEL1: z.string().min(1).optional(),
    VAS_DATA1: z.string().min(1).optional(),
    VAS_LABEL2: z.string().min(1).optional(),
    VAS_DATA2: z.string().min(1).optional(),
}) satisfies z.ZodType<ValorTerminalPublishPayloadV1>;

export const ValorTerminalPublishPayloadV2Schema: z.ZodType<ValorTerminalPublishPayloadV2> =
    ValorTerminalPublishPayloadV1Schema.extend({
        TIP_ENTRY: z.literal('1').optional(),
    });

export const ValorTerminalPublishGatewayRequestV1Schema: z.ZodType<ValorTerminalPublishGatewayRequestV1> =
    z.object({
        txn_type: z.literal('vc_publish'),
        channel_id: z.string(),
        version: z.string().min(1),
        payload: ValorTerminalPublishPayloadV1Schema,
    });

export const ValorTerminalPublishGatewayRequestV2Schema: z.ZodType<ValorTerminalPublishGatewayRequestV2> =
    z.object({
        txn_type: z.literal('vc_publish'),
        channel_id: z.string(),
        version: z.string().min(1),
        payload: ValorTerminalPublishPayloadV2Schema,
    });

export const ValorCardTokenSaleGatewayRequestSchema: z.ZodType<ValorCardTokenSaleGatewayRequest> =
    z.object({
        txn_type: z.literal('sale'),
        amount: z.string().min(1),
        surchargeIndicator: z.union([z.literal(0), z.literal(1)]),
        surchargeAmount: JsonLosslessNumberInputSchema,
        token: z.string().min(1).optional(),
        invoicenumber: z.string().min(1),
        cardholdername: z.string().min(1),
        address: z.string().min(1).optional(),
        city: z.string().min(1).optional(),
        orderdescription: z.string().min(1).optional(),
        shipping_country: z.string().min(1).optional(),
        zip: z.string().min(1).optional(),
        additional_prompts: z.array(ValorAdditionalPromptSchema).optional(),
    });

export function parseStoredValorCardTokenSaleGatewayRequest(
    value: unknown
): ValorCardTokenSaleGatewayRequest {
    return ValorCardTokenSaleGatewayRequestSchema.parse(
        normalizeStoredValorCardTokenSaleRequest(value)
    );
}

export const ValorVoidGatewayRequestSchema: z.ZodType<ValorVoidGatewayRequest> = z.object({
    txn_type: z.literal('void'),
    ref_txn_id: z.string().min(1),
    surchargeindicator: z.literal(1),
    amount: z.string().min(1).optional(),
});

export const ValorRefundPromptSchema: z.ZodType<ValorRefundPrompt> = z.object({
    name: z.string().min(1),
    value: z.string().min(1),
});

export const ValorRefundGatewayRequestSchema: z.ZodType<ValorRefundGatewayRequest> = z.object({
    txn_type: z.literal('refund'),
    amount: JsonLosslessNumberInputSchema,
    surchargeIndicator: z.union([z.literal(0), z.literal(1)]),
    ref_txn_id: z.string().min(1),
    sale_refund: z.literal('1'),
    merchantTransactionId: z.string().min(1),
    additionalPrompts: z.array(ValorRefundPromptSchema).optional(),
    rrn: z.string().min(1).optional(),
    auth_code: z.string().min(1).optional(),
    child_epi: z.string().min(1).optional(),
});

export const ValorTransactionStatusGatewayRequestSchema: z.ZodType<ValorTransactionStatusGatewayRequest> =
    z.object({
        REQ_TXN_ID: z.string().min(1).optional(),
        TXN_ID: z.string().min(1).optional(),
        channel_id: z.string().min(1).optional(),
    });

export const ValorTransactionListGatewayRequestSchema: z.ZodType<ValorTransactionListGatewayRequest> =
    z.object({
        date_filter: z.string().min(1).optional(),
        amount: z.string().min(1).optional(),
        ADDNL_VAL_1: z.string().min(1).optional(),
    });

export const ValorReconciliationGatewayRequestSchema: z.ZodType<ValorReconciliationGatewayRequest> =
    z.object({
        gateway_txn_id: z.string().min(1),
        merchant_transaction_id: z.string().min(1).optional(),
        amount: z.string().min(1).optional(),
    });

export const AchPaymentGatewayRequestSchema: z.ZodType<AchPaymentGatewayRequest> = z.object({
    txn_type: z.literal('ach'),
    account_id: z.string().min(1),
});

export const IntegrationReconciliationGatewayRequestSchema: z.ZodType<IntegrationReconciliationGatewayRequest> =
    z.object({
        source: z.string().min(1),
    });

export const TerminalSaleDispatchSourceDocumentKeySnapshotSchema: z.ZodType<TerminalSaleDispatchSourceDocumentKeySnapshot> =
    z
        .object({
            transaction_details_type_id: z.number().int(),
            transaction_details_type_version: z.number().int(),
        })
        .catchall(JsonValueSchema);

export const TerminalSaleDispatchTransactionDetailsSnapshotSchema: z.ZodType<TerminalSaleDispatchTransactionDetailsSnapshot> =
    z
        .object({
            amount: JsonLosslessNumberInputSchema,
            currency: z.string().min(1),
            ipw_system_id: z.number().int(),
            transaction_details_type_id: z.number().int(),
            transaction_details_type_version: z.number().int(),
        })
        .catchall(JsonValueSchema);

export const TerminalSaleDispatchGatewayRequestSchema: z.ZodType<TerminalSaleDispatchGatewayRequest> =
    z.object({
        amount: z.string().min(1),
        merchant_transaction_id: z.string().min(1),
        source_document_key: TerminalSaleDispatchSourceDocumentKeySnapshotSchema,
        source_document_name: z.string().min(1),
        source_document_reference: z.string().min(1),
        transaction_details: TerminalSaleDispatchTransactionDetailsSnapshotSchema,
    });

export const ValorTerminalPublishGatewayLifecycleRequestV1Schema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.VALOR_TERMINAL_PUBLISH_V1,
    typeof ValorTerminalPublishGatewayRequestV1Schema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.VALOR_TERMINAL_PUBLISH_V1,
    ValorTerminalPublishGatewayRequestV1Schema
) satisfies z.ZodType<ValorTerminalPublishGatewayLifecycleRequestV1>;

export const ValorTerminalPublishGatewayLifecycleRequestV2Schema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.VALOR_TERMINAL_PUBLISH_V2,
    typeof ValorTerminalPublishGatewayRequestV2Schema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.VALOR_TERMINAL_PUBLISH_V2,
    ValorTerminalPublishGatewayRequestV2Schema
) satisfies z.ZodType<ValorTerminalPublishGatewayLifecycleRequestV2>;

export const ValorCardTokenSaleGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.VALOR_CARD_TOKEN_SALE,
    typeof ValorCardTokenSaleGatewayRequestSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.VALOR_CARD_TOKEN_SALE,
    ValorCardTokenSaleGatewayRequestSchema
) satisfies z.ZodType<ValorCardTokenSaleGatewayLifecycleRequest>;

export const ValorVoidGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.VALOR_VOID,
    typeof ValorVoidGatewayRequestSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.VALOR_VOID,
    ValorVoidGatewayRequestSchema
) satisfies z.ZodType<ValorVoidGatewayLifecycleRequest>;

export const ValorRefundGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.VALOR_REFUND,
    typeof ValorRefundGatewayRequestSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.VALOR_REFUND,
    ValorRefundGatewayRequestSchema
) satisfies z.ZodType<ValorRefundGatewayLifecycleRequest>;

export const ValorTransactionStatusGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.VALOR_TRANSACTION_STATUS,
    typeof ValorTransactionStatusGatewayRequestSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.VALOR_TRANSACTION_STATUS,
    ValorTransactionStatusGatewayRequestSchema
) satisfies z.ZodType<ValorTransactionStatusGatewayLifecycleRequest>;

export const ValorTransactionListGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.VALOR_TRANSACTION_LIST,
    typeof ValorTransactionListGatewayRequestSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.VALOR_TRANSACTION_LIST,
    ValorTransactionListGatewayRequestSchema
) satisfies z.ZodType<ValorTransactionListGatewayLifecycleRequest>;

export const ValorReconciliationGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.VALOR_RECONCILIATION,
    typeof ValorReconciliationGatewayRequestSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.VALOR_RECONCILIATION,
    ValorReconciliationGatewayRequestSchema
) satisfies z.ZodType<ValorReconciliationGatewayLifecycleRequest>;

export const CardPointeCnpTokenSaleGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CARDPOINTE_CNP_TOKEN_SALE,
    typeof CardPointeCnpTokenSaleRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CARDPOINTE_CNP_TOKEN_SALE,
    CardPointeCnpTokenSaleRequestDetailsSchema
) satisfies z.ZodType<CardPointeCnpTokenSaleGatewayLifecycleRequest>;

export const CardPointeTerminalSaleGatewayLifecycleRequestV1Schema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CARDPOINTE_TERMINAL_SALE_V1,
    typeof StoredCardPointeTerminalSaleRequestLifecyclePayloadV1Schema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CARDPOINTE_TERMINAL_SALE_V1,
    StoredCardPointeTerminalSaleRequestLifecyclePayloadV1Schema
) satisfies z.ZodType<CardPointeTerminalSaleGatewayLifecycleRequestV1>;

export const CardPointeTerminalSaleGatewayLifecycleRequestV2Schema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CARDPOINTE_TERMINAL_SALE_V2,
    typeof CardPointeTerminalSaleRequestLifecyclePayloadV2Schema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CARDPOINTE_TERMINAL_SALE_V2,
    CardPointeTerminalSaleRequestLifecyclePayloadV2Schema
) satisfies z.ZodType<CardPointeTerminalSaleGatewayLifecycleRequestV2>;

export const CardPointeLifecyclePreflightGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CARDPOINTE_LIFECYCLE_PREFLIGHT,
    typeof StoredCardPointeLifecyclePreflightRequestPayloadSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CARDPOINTE_LIFECYCLE_PREFLIGHT,
    StoredCardPointeLifecyclePreflightRequestPayloadSchema
) satisfies z.ZodType<CardPointeLifecyclePreflightGatewayLifecycleRequest>;

export const CardPointeLifecycleFinalizationGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CARDPOINTE_LIFECYCLE_FINALIZATION,
    typeof CardPointeLifecycleFinalizationRequestPayloadSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CARDPOINTE_LIFECYCLE_FINALIZATION,
    CardPointeLifecycleFinalizationRequestPayloadSchema
) satisfies z.ZodType<CardPointeLifecycleFinalizationGatewayLifecycleRequest>;

export const CardPointeTerminalTokenizationGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CARDPOINTE_TERMINAL_TOKENIZATION,
    typeof CardPointeTerminalTokenizationRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CARDPOINTE_TERMINAL_TOKENIZATION,
    CardPointeTerminalTokenizationRequestDetailsSchema
) satisfies z.ZodType<CardPointeTerminalTokenizationGatewayLifecycleRequest>;

export const CardPointeTransactionStatusGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CARDPOINTE_TRANSACTION_STATUS,
    typeof CardPointeTransactionStatusRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CARDPOINTE_TRANSACTION_STATUS,
    CardPointeTransactionStatusRequestDetailsSchema
) satisfies z.ZodType<CardPointeTransactionStatusGatewayLifecycleRequest>;

export const CardPointeInquireGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CARDPOINTE_INQUIRE,
    typeof CardPointeTransactionDetailsRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CARDPOINTE_INQUIRE,
    CardPointeTransactionDetailsRequestDetailsSchema
) satisfies z.ZodType<CardPointeInquireGatewayLifecycleRequest>;

export const CardPointeVoidGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CARDPOINTE_VOID,
    typeof CardPointeVoidRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CARDPOINTE_VOID,
    CardPointeVoidRequestDetailsSchema
) satisfies z.ZodType<CardPointeVoidGatewayLifecycleRequest>;

export const CardPointeRefundGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CARDPOINTE_REFUND,
    typeof CardPointeRefundRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CARDPOINTE_REFUND,
    CardPointeRefundRequestDetailsSchema
) satisfies z.ZodType<CardPointeRefundGatewayLifecycleRequest>;

export const CardPointeReconciliationGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CARDPOINTE_RECONCILIATION,
    typeof CardPointeReconciliationLookupRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CARDPOINTE_RECONCILIATION,
    CardPointeReconciliationLookupRequestDetailsSchema
) satisfies z.ZodType<CardPointeReconciliationGatewayLifecycleRequest>;

const CardPointeStoredCardSaleLegacyRequestBodyBaseSchema = z.object({
    merchid: z.string().min(1),
    account: z.string().min(1),
    amount: CardPointeCnpTokenSaleAmountSchema,
    currency: z.string().length(3),
    capture: z.union([z.literal('y'), z.literal('n')]),
    orderid: z.string().min(1).optional(),
    name: z.string().min(1).optional(),
    expiry: z
        .string()
        .regex(/^\d{4}$/)
        .optional(),
    address: z.string().min(1).optional(),
    city: z.string().min(1).optional(),
    region: z.string().min(1).optional(),
    country: z.string().min(1).optional(),
    postal: z.string().min(1).optional(),
});

export const CardPointeStoredCardSaleLegacyRequestBodySchema: z.ZodType<CardPointeStoredCardSaleLegacyRequestBody> =
    z.union([
        CardPointeStoredCardSaleLegacyRequestBodyBaseSchema.strict(),
        CardPointeStoredCardSaleLegacyRequestBodyBaseSchema.extend({
            cof: z.literal('C'),
            cofscheduled: z.literal('N'),
        }).strict(),
        CardPointeStoredCardSaleLegacyRequestBodyBaseSchema.extend({
            cof: z.literal('M'),
            cofscheduled: z.union([z.literal('N'), z.literal('Y')]),
        }).strict(),
    ]);

export const CardPointeStoredCardSaleLegacyRequestDetailsSchema: z.ZodType<CardPointeStoredCardSaleLegacyRequestDetails> =
    z.object({
        method: z.literal('PUT'),
        path: z.literal('/cardconnect/rest/auth'),
        body: CardPointeStoredCardSaleLegacyRequestBodySchema,
    });

export const StoredCardPointeStoredCardSaleRequestDetailsSchema: z.ZodType<StoredCardPointeStoredCardSaleRequestDetails> =
    z.union([
        CardPointeCnpTokenSaleRequestDetailsSchema,
        CardPointeStoredCardSaleLegacyRequestDetailsSchema,
    ]);

export const CardPointeStoredCardSaleGatewayLifecycleRequestV1Schema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CARDPOINTE_STORED_CARD_SALE_V1,
    typeof StoredCardPointeStoredCardSaleRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CARDPOINTE_STORED_CARD_SALE_V1,
    StoredCardPointeStoredCardSaleRequestDetailsSchema
) satisfies z.ZodType<CardPointeStoredCardSaleGatewayLifecycleRequestV1>;

export const CardPointeStoredCardSaleGatewayLifecycleRequestV2Schema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CARDPOINTE_STORED_CARD_SALE_V2,
    typeof CardPointeCnpTokenSaleRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CARDPOINTE_STORED_CARD_SALE_V2,
    CardPointeCnpTokenSaleRequestDetailsSchema
) satisfies z.ZodType<CardPointeStoredCardSaleGatewayLifecycleRequestV2>;

export const CloverCnpTokenSaleGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CLOVER_CNP_TOKEN_SALE,
    typeof CloverCnpTokenSaleRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CLOVER_CNP_TOKEN_SALE,
    CloverCnpTokenSaleRequestDetailsSchema
) satisfies z.ZodType<CloverCnpTokenSaleGatewayLifecycleRequest>;

export const CloverTerminalSaleGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CLOVER_TERMINAL_SALE,
    typeof CloverTerminalSaleRequestLifecyclePayloadSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CLOVER_TERMINAL_SALE,
    CloverTerminalSaleRequestLifecyclePayloadSchema
) satisfies z.ZodType<CloverTerminalSaleGatewayLifecycleRequest>;

export const CloverLifecyclePreflightGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CLOVER_LIFECYCLE_PREFLIGHT,
    typeof CloverLifecyclePreflightRequestPayloadSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CLOVER_LIFECYCLE_PREFLIGHT,
    CloverLifecyclePreflightRequestPayloadSchema
) satisfies z.ZodType<CloverLifecyclePreflightGatewayLifecycleRequest>;

export const CloverLifecycleFinalizationGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CLOVER_LIFECYCLE_FINALIZATION,
    typeof CloverLifecycleFinalizationRequestPayloadSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CLOVER_LIFECYCLE_FINALIZATION,
    CloverLifecycleFinalizationRequestPayloadSchema
) satisfies z.ZodType<CloverLifecycleFinalizationGatewayLifecycleRequest>;

export const CloverTerminalTokenizationGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CLOVER_TERMINAL_TOKENIZATION,
    typeof CloverTerminalTokenizationRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CLOVER_TERMINAL_TOKENIZATION,
    CloverTerminalTokenizationRequestDetailsSchema
) satisfies z.ZodType<CloverTerminalTokenizationGatewayLifecycleRequest>;

export const CloverTransactionStatusGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CLOVER_TRANSACTION_STATUS,
    typeof CloverTransactionStatusRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CLOVER_TRANSACTION_STATUS,
    CloverTransactionStatusRequestDetailsSchema
) satisfies z.ZodType<CloverTransactionStatusGatewayLifecycleRequest>;

export const CloverVoidGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CLOVER_VOID,
    typeof CloverVoidRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CLOVER_VOID,
    CloverVoidRequestDetailsSchema
) satisfies z.ZodType<CloverVoidGatewayLifecycleRequest>;

export const CloverRefundGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CLOVER_REFUND,
    typeof CloverRefundRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CLOVER_REFUND,
    CloverRefundRequestDetailsSchema
) satisfies z.ZodType<CloverRefundGatewayLifecycleRequest>;

export const CloverReconciliationGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CLOVER_RECONCILIATION,
    typeof CloverReconciliationLookupRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CLOVER_RECONCILIATION,
    CloverReconciliationLookupRequestDetailsSchema
) satisfies z.ZodType<CloverReconciliationGatewayLifecycleRequest>;

export const CloverStoredCardSaleGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.CLOVER_STORED_CARD_SALE,
    typeof CloverCnpTokenSaleRequestDetailsSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.CLOVER_STORED_CARD_SALE,
    CloverCnpTokenSaleRequestDetailsSchema
) satisfies z.ZodType<CloverStoredCardSaleGatewayLifecycleRequest>;

export const AchPaymentGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.ACH_PAYMENT,
    typeof AchPaymentGatewayRequestSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.ACH_PAYMENT,
    AchPaymentGatewayRequestSchema
) satisfies z.ZodType<AchPaymentGatewayLifecycleRequest>;

export const IntegrationReconciliationGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.INTEGRATION_RECONCILIATION,
    typeof IntegrationReconciliationGatewayRequestSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.INTEGRATION_RECONCILIATION,
    IntegrationReconciliationGatewayRequestSchema
) satisfies z.ZodType<IntegrationReconciliationGatewayLifecycleRequest>;

export const TerminalSaleDispatchGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.TERMINAL_SALE_DISPATCH,
    typeof TerminalSaleDispatchGatewayRequestSchema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.TERMINAL_SALE_DISPATCH,
    TerminalSaleDispatchGatewayRequestSchema
) satisfies z.ZodType<TerminalSaleDispatchGatewayLifecycleRequest>;

export const BinLookupEvidenceV1Schema: z.ZodType<BinLookupEvidenceV1> = z.discriminatedUnion(
    'bin_lookup_backend',
    [
        z.object({
            bin_lookup_evidence_version: z.literal(1),
            bin_lookup_backend: z.literal(BinLookupBackend.VALOR),
            pan_prefix: z.string().regex(/^\d{6,9}$/),
            card_type: z.enum(CardType),
            provider_response: JsonValueSchema,
        }),
        z.object({
            bin_lookup_evidence_version: z.literal(1),
            bin_lookup_backend: z.literal(BinLookupBackend.BINLOOKUPAPI),
            pan_prefix: z.string().regex(/^\d{6,9}$/),
            card_type: z.enum(CardType),
            provider_response: JsonValueSchema,
        }),
    ]
);

export const BinLookupGatewayLifecycleRequestSchema: GatewayLifecycleRequestVariantSchema<
    typeof GatewayLifecycleRequestType.BIN_LOOKUP,
    typeof BinLookupEvidenceV1Schema
> = gatewayLifecycleRequestVariantSchema(
    GatewayLifecycleRequestType.BIN_LOOKUP,
    BinLookupEvidenceV1Schema
) satisfies z.ZodType<BinLookupGatewayLifecycleRequest>;

export const CloverRawGatewayLifecycleRequestTypeSchema = z.union([
    z.literal(GatewayLifecycleRequestType.CLOVER_BROWSER_TOKENIZATION),
    z.literal(GatewayLifecycleRequestType.CLOVER_CNP_TOKEN_SALE_V2),
    z.literal(GatewayLifecycleRequestType.CLOVER_RECONCILIATION_V2),
    z.literal(GatewayLifecycleRequestType.CLOVER_REFUND_V2),
    z.literal(GatewayLifecycleRequestType.CLOVER_STORED_CARD_SALE_V2),
    z.literal(GatewayLifecycleRequestType.CLOVER_TERMINAL_CANCEL),
    z.literal(GatewayLifecycleRequestType.CLOVER_TERMINAL_SALE_V2),
    z.literal(GatewayLifecycleRequestType.CLOVER_TERMINAL_TOKENIZATION_V2),
    z.literal(GatewayLifecycleRequestType.CLOVER_TRANSACTION_DETAILS),
    z.literal(GatewayLifecycleRequestType.CLOVER_TRANSACTION_STATUS_V2),
    z.literal(GatewayLifecycleRequestType.CLOVER_WEBHOOK_PAYMENT_RETRIEVAL),
    z.literal(GatewayLifecycleRequestType.CLOVER_VOID_V2),
]);

export const CardPointeRawGatewayLifecycleRequestTypeSchema = z.union([
    z.literal(GatewayLifecycleRequestType.CARDPOINTE_CNP_TOKEN_SALE_V2),
    z.literal(GatewayLifecycleRequestType.CARDPOINTE_FUNDING_V2),
    z.literal(GatewayLifecycleRequestType.CARDPOINTE_INQUIRE_V2),
    z.literal(GatewayLifecycleRequestType.CARDPOINTE_REFUND_V2),
    z.literal(GatewayLifecycleRequestType.CARDPOINTE_STORED_CARD_SALE_V3),
    z.literal(GatewayLifecycleRequestType.CARDPOINTE_TERMINAL_CANCEL_V2),
    z.literal(GatewayLifecycleRequestType.CARDPOINTE_TERMINAL_SALE_V3),
    z.literal(GatewayLifecycleRequestType.CARDPOINTE_TERMINAL_TOKENIZATION_V2),
    z.literal(GatewayLifecycleRequestType.CARDPOINTE_TRANSACTION_STATUS_V2),
    z.literal(GatewayLifecycleRequestType.CARDPOINTE_VOID_V2),
]);

export const ValorRawGatewayLifecycleRequestTypeSchema = z.union([
    z.literal(GatewayLifecycleRequestType.VALOR_BROWSER_TOKENIZATION),
    z.literal(GatewayLifecycleRequestType.VALOR_CARD_TOKEN_SALE_V2),
    z.literal(GatewayLifecycleRequestType.VALOR_RECONCILIATION_V2),
    z.literal(GatewayLifecycleRequestType.VALOR_REFUND_V2),
    z.literal(GatewayLifecycleRequestType.VALOR_TERMINAL_CANCEL),
    z.literal(GatewayLifecycleRequestType.VALOR_TERMINAL_PUBLISH_V3),
    z.literal(GatewayLifecycleRequestType.VALOR_TRANSACTION_DETAILS),
    z.literal(GatewayLifecycleRequestType.VALOR_TRANSACTION_LIST_V2),
    z.literal(GatewayLifecycleRequestType.VALOR_TRANSACTION_STATUS_V2),
    z.literal(GatewayLifecycleRequestType.VALOR_VOID_V2),
]);

export const CardPointeRawGatewayLifecycleRequestSchema: z.ZodType<CardPointeRawGatewayLifecycleRequest> =
    z.object({
        gateway_request_type: CardPointeRawGatewayLifecycleRequestTypeSchema,
        gateway_request: JsonObjectSchema,
    });

export const StoredCardPointeRawGatewayLifecycleRequestSchema: z.ZodType<CardPointeRawGatewayLifecycleRequest> =
    z.object({
        gateway_request_type: CardPointeRawGatewayLifecycleRequestTypeSchema,
        gateway_request: z.custom<ProviderGatewayRequestSnapshot>(
            (value: unknown): value is ProviderGatewayRequestSnapshot =>
                StoredJsonObjectSchema.safeParse(value).success,
            { message: 'Invalid stored raw CardPointe gateway request snapshot' }
        ),
    });

export const CloverRawGatewayLifecycleRequestSchema: z.ZodType<CloverRawGatewayLifecycleRequest> =
    z.object({
        gateway_request_type: CloverRawGatewayLifecycleRequestTypeSchema,
        gateway_request: JsonObjectSchema,
    });

export const StoredCloverRawGatewayLifecycleRequestSchema: z.ZodType<CloverRawGatewayLifecycleRequest> =
    z.object({
        gateway_request_type: CloverRawGatewayLifecycleRequestTypeSchema,
        gateway_request: z.custom<ProviderGatewayRequestSnapshot>(
            (value: unknown): value is ProviderGatewayRequestSnapshot =>
                StoredJsonObjectSchema.safeParse(value).success,
            { message: 'Invalid stored provider gateway request snapshot' }
        ),
    });

export const ValorRawGatewayLifecycleRequestSchema: z.ZodType<ValorRawGatewayLifecycleRequest> =
    z.object({
        gateway_request_type: ValorRawGatewayLifecycleRequestTypeSchema,
        gateway_request: JsonObjectSchema,
    });

export const StoredValorRawGatewayLifecycleRequestSchema: z.ZodType<ValorRawGatewayLifecycleRequest> =
    z.object({
        gateway_request_type: ValorRawGatewayLifecycleRequestTypeSchema,
        gateway_request: z.custom<ProviderGatewayRequestSnapshot>(
            (value: unknown): value is ProviderGatewayRequestSnapshot =>
                StoredJsonObjectSchema.safeParse(value).success,
            { message: 'Invalid stored raw Valor gateway request snapshot' }
        ),
    });

export const GatewayLifecycleRequestSchema: z.ZodType<GatewayLifecycleRequest> = z.union([
    CardPointeRawGatewayLifecycleRequestSchema,
    CloverRawGatewayLifecycleRequestSchema,
    ValorRawGatewayLifecycleRequestSchema,
    BinLookupGatewayLifecycleRequestSchema,
    AchPaymentGatewayLifecycleRequestSchema,
    CardPointeCnpTokenSaleGatewayLifecycleRequestSchema,
    CardPointeInquireGatewayLifecycleRequestSchema,
    CardPointeLifecycleFinalizationGatewayLifecycleRequestSchema,
    CardPointeLifecyclePreflightGatewayLifecycleRequestSchema,
    CardPointeReconciliationGatewayLifecycleRequestSchema,
    CardPointeRefundGatewayLifecycleRequestSchema,
    CardPointeStoredCardSaleGatewayLifecycleRequestV1Schema,
    CardPointeStoredCardSaleGatewayLifecycleRequestV2Schema,
    CardPointeTerminalSaleGatewayLifecycleRequestV1Schema,
    CardPointeTerminalSaleGatewayLifecycleRequestV2Schema,
    CardPointeTerminalTokenizationGatewayLifecycleRequestSchema,
    CardPointeTransactionStatusGatewayLifecycleRequestSchema,
    CardPointeVoidGatewayLifecycleRequestSchema,
    CloverCnpTokenSaleGatewayLifecycleRequestSchema,
    CloverLifecycleFinalizationGatewayLifecycleRequestSchema,
    CloverLifecyclePreflightGatewayLifecycleRequestSchema,
    CloverReconciliationGatewayLifecycleRequestSchema,
    CloverRefundGatewayLifecycleRequestSchema,
    CloverStoredCardSaleGatewayLifecycleRequestSchema,
    CloverTerminalSaleGatewayLifecycleRequestSchema,
    CloverTerminalTokenizationGatewayLifecycleRequestSchema,
    CloverTransactionStatusGatewayLifecycleRequestSchema,
    CloverVoidGatewayLifecycleRequestSchema,
    IntegrationReconciliationGatewayLifecycleRequestSchema,
    TerminalSaleDispatchGatewayLifecycleRequestSchema,
    ValorCardTokenSaleGatewayLifecycleRequestSchema,
    ValorReconciliationGatewayLifecycleRequestSchema,
    ValorRefundGatewayLifecycleRequestSchema,
    ValorTerminalPublishGatewayLifecycleRequestV1Schema,
    ValorTerminalPublishGatewayLifecycleRequestV2Schema,
    ValorTransactionListGatewayLifecycleRequestSchema,
    ValorTransactionStatusGatewayLifecycleRequestSchema,
    ValorVoidGatewayLifecycleRequestSchema,
]);

export const StoredGatewayLifecycleRequestSchema: z.ZodType<GatewayLifecycleRequest> = z.preprocess(
    normalizeStoredGatewayLifecycleRequestNumericLeaves,
    z.union([
        StoredCardPointeRawGatewayLifecycleRequestSchema,
        StoredCloverRawGatewayLifecycleRequestSchema,
        StoredValorRawGatewayLifecycleRequestSchema,
        GatewayLifecycleRequestSchema,
    ])
);

export function normalizeGatewayLifecycleRequestStorageV1(
    request: GatewayLifecycleRequest
): GatewayLifecycleRequest {
    return request;
}

export const GatewayLifecycleRequestStorageContract = defineStoredJsonContract({
    contractName: 'GatewayLifecycleRequest',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'StoredGatewayLifecycleRequestSchema',
            schema: StoredGatewayLifecycleRequestSchema,
            schemaInput: (boundary: StoredJsonRowBoundary) => ({
                gateway_request_type: boundary.siblingValues.gateway_request_type,
                gateway_request: boundary.value,
            }),
            guards: [
                {
                    kind: 'sibling_field_present',
                    siblingField: 'gateway_request_type',
                },
            ],
            normalize: normalizeGatewayLifecycleRequestStorageV1,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v1',
});

export const StoredGatewayLifecycleRequestRowParser: z.ZodType<GatewayLifecycleRequest> =
    buildStoredJsonParser(GatewayLifecycleRequestStorageContract);

export function parseGatewayLifecycleRequestParts(params: {
    readonly requestType: string;
    readonly payload: unknown;
    readonly context: string;
}): GatewayLifecycleRequest {
    const parseResult = GatewayLifecycleRequestSchema.safeParse({
        gateway_request_type: params.requestType,
        gateway_request: params.payload,
    });
    if (!parseResult.success) {
        throw new Error(
            `Invalid gateway_request for ${params.context} type ${params.requestType}: ${parseResult.error.message}`
        );
    }
    return parseResult.data;
}

export function parseStoredGatewayLifecycleRequestParts(params: {
    readonly requestType: string;
    readonly payload: unknown;
    readonly context: string;
}): GatewayLifecycleRequest {
    const normalized = normalizeStoredGatewayLifecycleRequestParts(params);
    const parseResult = StoredGatewayLifecycleRequestSchema.safeParse({
        gateway_request_type: normalized.requestType,
        gateway_request: normalized.payload,
    });
    if (!parseResult.success) {
        throw new Error(
            `Invalid gateway_request for ${params.context} type ${params.requestType}: ${parseResult.error.message}`
        );
    }
    return parseResult.data;
}

export function normalizeStoredGatewayLifecycleRequestParts(params: {
    readonly requestType: string;
    readonly payload: unknown;
}): { readonly requestType: string; readonly payload: unknown } {
    const legacyEnvelope = StoredJsonNumberRecordSchema.safeParse(params.payload);
    const legacyRequestType = legacyEnvelope.success
        ? z.string().safeParse(legacyEnvelope.data.gateway_request_type)
        : undefined;
    if (
        legacyEnvelope.success &&
        legacyRequestType?.success === true &&
        legacyEnvelope.data.gateway_request !== undefined
    ) {
        return {
            requestType: legacyRequestType.data,
            payload: legacyEnvelope.data.gateway_request,
        };
    }
    return params;
}
